<template>
  <div class="flex h-screen bg-[#F8FAFC] font-sans text-slate-900 overflow-hidden">
    <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Inter:wght@400;500;600;700;800&family=Poppins:wght@400;500;600;700;800&display=swap" rel="stylesheet">

    <!-- Sidebar Gauche -->
    <aside class="w-72 bg-white border-r border-slate-200 flex flex-col z-20 shadow-sm">
      <div class="p-6 border-b border-slate-100">
        <h1 class="text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">Sélection Langue</h1>
        <p class="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">Écran d'accueil</p>
      </div>

      <div class="flex-1 overflow-y-auto p-4 space-y-6">
        <!-- Langues actives -->
        <div>
          <h3 class="text-xs font-bold text-slate-400 uppercase mb-3 px-2">Langues Disponibles</h3>
          <div class="space-y-2">
            <div v-for="(lang, idx) in config.enabledLanguages" :key="idx"
                 class="flex items-center gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100">
              <span class="text-2xl">{{ lang.flag }}</span>
              <div class="flex-1">
                <div class="font-semibold text-sm">{{ lang.label }}</div>
                <div class="text-xs text-slate-400">{{ lang.code }}</div>
              </div>
              <button @click="removeLanguage(idx)" class="text-red-500 hover:bg-red-50 p-1 rounded">✕</button>
            </div>
          </div>

          <button @click="showAddLanguageMenu = true" class="w-full mt-3 py-2 border-2 border-dashed border-slate-200 rounded-xl text-slate-400 text-xs font-bold hover:border-blue-400 hover:text-blue-500 transition-all">
            + Ajouter une langue
          </button>
        </div>

        <!-- Style d'affichage -->
        <div class="pt-4 border-t">
          <h3 class="text-xs font-bold text-slate-400 uppercase mb-3 px-2">Style d'affichage</h3>
          <div class="space-y-2 px-2">
            <label class="text-[10px] text-slate-500 mb-1 block">Layout</label>
            <select v-model="config.displayStyle.type" class="w-full p-2 bg-slate-50 rounded-lg border text-xs">
              <option value="cards">Cartes</option>
              <option value="list">Liste</option>
              <option value="grid">Grille</option>
            </select>

            <div v-if="config.displayStyle.type === 'grid'">
              <label class="text-[10px] text-slate-500 mb-1 block">Colonnes</label>
              <input type="number" v-model.number="config.displayStyle.columns" min="1" max="4" class="w-full p-2 bg-slate-50 rounded-lg border text-xs" />
            </div>
          </div>
        </div>
      </div>

      <div class="p-4 border-t border-slate-100 space-y-2">
        <!-- Boutons Import/Export -->
        <div class="flex gap-2">
          <button @click="exportConfig"
                  class="flex-1 py-2 bg-white border border-slate-200 rounded-lg hover:border-green-400 text-xs font-semibold text-slate-700 hover:text-green-600 transition-all">
            📥 Export
          </button>
          <label class="flex-1 py-2 bg-white border border-slate-200 rounded-lg hover:border-green-400 text-xs font-semibold text-slate-700 hover:text-green-600 transition-all cursor-pointer text-center">
            📤 Import
            <input type="file" accept=".json" @change="importConfig" class="hidden" />
          </label>
        </div>

        <button @click="saveConfig" class="w-full py-4 bg-slate-900 text-white rounded-2xl font-bold shadow-xl hover:bg-blue-600 transition-all flex items-center justify-center gap-2">
          <span>🚀 Publier</span>
        </button>
      </div>
    </aside>

    <!-- Canvas Preview -->
    <main class="flex-1 relative bg-slate-100 overflow-hidden p-12 flex justify-center items-center">
      <div class="relative bg-slate-800 p-4 rounded-[4rem] shadow-[0_50px_100px_-20px_rgba(0,0,0,0.4)] border-[8px] border-slate-700">
        <div
          class="relative overflow-hidden bg-white w-[375px] h-[667px] rounded-[2rem] shadow-inner flex flex-col items-center justify-center p-8"
          :style="getBackgroundStyle()"
        >
          <!-- Logo -->
          <div v-if="config.branding.logo.show && config.branding.logo.url"
               class="mb-6"
               :class="config.branding.logo.position === 'top' ? 'absolute top-12' : ''">
            <img :src="config.branding.logo.url"
                 :style="{ width: (config.branding.logo.size / SCALE_FACTOR) + 'px', height: 'auto' }"
                 class="object-contain" />
          </div>

          <!-- Header Text -->
          <div v-if="config.branding.header.show"
               class="mb-8 text-center"
               :style="{
                 fontSize: (config.branding.header.fontSize / SCALE_FACTOR) + 'px',
                 fontFamily: config.branding.header.fontFamily,
                 color: config.branding.header.color,
                 fontWeight: '700'
               }">
            {{ config.branding.header.text }}
          </div>

          <!-- Language Buttons -->
          <div class="flex flex-col gap-3 w-full max-w-[300px]"
               :class="config.displayStyle.type === 'grid' ? `grid grid-cols-${config.displayStyle.columns}` : ''">
            <button v-for="lang in config.enabledLanguages" :key="lang.code"
                    class="flex items-center gap-3 p-4 rounded-xl transition-all border-2"
                    :style="{
                      backgroundColor: config.branding.buttonStyle.backgroundColor,
                      color: config.branding.buttonStyle.textColor,
                      borderColor: config.branding.buttonStyle.borderColor,
                      borderRadius: (config.branding.buttonStyle.borderRadius / SCALE_FACTOR) + 'px',
                      borderWidth: (config.branding.buttonStyle.borderWidth / SCALE_FACTOR) + 'px'
                    }">
              <span class="text-2xl">{{ lang.flag }}</span>
              <span class="font-semibold text-sm">{{ lang.label }}</span>
            </button>
          </div>
        </div>
      </div>
    </main>

    <!-- Sidebar Droite - Propriétés -->
    <aside class="w-80 bg-white border-l border-slate-200 flex flex-col z-20 shadow-xl overflow-hidden">
      <div class="p-6 border-b border-slate-100 bg-blue-50/50">
        <h2 class="font-bold text-slate-800 uppercase text-xs">Propriétés</h2>
      </div>

      <div class="flex-1 overflow-y-auto p-6 space-y-6">
        <!-- Background -->
        <div class="space-y-3">
          <label class="text-[10px] font-black text-slate-400 uppercase">Fond d'écran</label>
          <select v-model="config.branding.backgroundType" class="w-full p-2 bg-slate-50 rounded-lg border text-xs">
            <option value="color">Couleur unie</option>
            <option value="gradient">Dégradé</option>
            <option value="image">Image</option>
          </select>

          <div v-if="config.branding.backgroundType === 'color'">
            <label class="text-[10px] text-slate-500 mb-1 block">Couleur</label>
            <input type="color" v-model="config.branding.backgroundColor" class="w-full h-10 rounded cursor-pointer" />
          </div>

          <div v-if="config.branding.backgroundType === 'gradient'" class="space-y-2">
            <div>
              <label class="text-[10px] text-slate-500 mb-1 block">Couleur 1</label>
              <input type="color" v-model="config.branding.gradient.color1" class="w-full h-8 rounded cursor-pointer" />
            </div>
            <div>
              <label class="text-[10px] text-slate-500 mb-1 block">Couleur 2</label>
              <input type="color" v-model="config.branding.gradient.color2" class="w-full h-8 rounded cursor-pointer" />
            </div>
            <div>
              <label class="text-[10px] text-slate-500 mb-1 block">Direction</label>
              <select v-model="config.branding.gradient.direction" class="w-full p-2 bg-slate-50 rounded-lg border text-xs">
                <option value="to right">Horizontal →</option>
                <option value="to bottom">Vertical ↓</option>
                <option value="to bottom right">Diagonal ↘</option>
              </select>
            </div>
          </div>

          <div v-if="config.branding.backgroundType === 'image'">
            <label class="text-[10px] text-slate-500 mb-1 block">URL de l'image</label>
            <MediaPicker
              v-model="config.branding.backgroundImage"
              placeholder="URL de l'image de fond"
              button-text="Choisir l'image de fond"
              :agency-id="agencyId"
            />
          </div>
        </div>

        <!-- Header -->
        <div class="space-y-3 pt-4 border-t">
          <label class="text-[10px] font-black text-slate-400 uppercase">En-tête</label>
          <div class="flex items-center gap-2">
            <input type="checkbox" v-model="config.branding.header.show" id="showHeader" />
            <label for="showHeader" class="text-xs">Afficher l'en-tête</label>
          </div>

          <div v-if="config.branding.header.show" class="space-y-2">
            <div>
              <label class="text-[10px] text-slate-500 mb-1 block">Texte</label>
              <input type="text" v-model="config.branding.header.text" class="w-full p-2 bg-slate-50 rounded-lg border text-xs" />
            </div>
            <div class="grid grid-cols-2 gap-2">
              <div>
                <label class="text-[10px] text-slate-500 mb-1 block">Taille</label>
                <input type="number" v-model.number="config.branding.header.fontSize" class="w-full p-2 bg-slate-50 rounded-lg border text-xs" />
              </div>
              <div>
                <label class="text-[10px] text-slate-500 mb-1 block">Couleur</label>
                <input type="color" v-model="config.branding.header.color" class="w-full h-9 rounded cursor-pointer" />
              </div>
            </div>
          </div>
        </div>

        <!-- Logo -->
        <div class="space-y-3 pt-4 border-t">
          <label class="text-[10px] font-black text-slate-400 uppercase">Logo</label>
          <div class="flex items-center gap-2">
            <input type="checkbox" v-model="config.branding.logo.show" id="showLogo" />
            <label for="showLogo" class="text-xs">Afficher le logo</label>
          </div>

          <div v-if="config.branding.logo.show" class="space-y-2">
            <div>
              <label class="text-[10px] text-slate-500 mb-1 block">URL du logo</label>
              <MediaPicker
                v-model="config.branding.logo.url"
                placeholder="URL du logo"
                button-text="Choisir le logo"
                :agency-id="agencyId"
              />
            </div>
            <div>
              <label class="text-[10px] text-slate-500 mb-1 block">Taille</label>
              <input type="number" v-model.number="config.branding.logo.size" class="w-full p-2 bg-slate-50 rounded-lg border text-xs" />
            </div>
            <div>
              <label class="text-[10px] text-slate-500 mb-1 block">Position</label>
              <select v-model="config.branding.logo.position" class="w-full p-2 bg-slate-50 rounded-lg border text-xs">
                <option value="top">En haut</option>
                <option value="center">Au centre</option>
              </select>
            </div>
          </div>
        </div>

        <!-- Button Style -->
        <div class="space-y-3 pt-4 border-t">
          <label class="text-[10px] font-black text-slate-400 uppercase">Style des boutons</label>
          <div class="grid grid-cols-2 gap-2">
            <div>
              <label class="text-[10px] text-slate-500 mb-1 block">Fond</label>
              <input type="color" v-model="config.branding.buttonStyle.backgroundColor" class="w-full h-8 rounded cursor-pointer" />
            </div>
            <div>
              <label class="text-[10px] text-slate-500 mb-1 block">Texte</label>
              <input type="color" v-model="config.branding.buttonStyle.textColor" class="w-full h-8 rounded cursor-pointer" />
            </div>
            <div>
              <label class="text-[10px] text-slate-500 mb-1 block">Bordure</label>
              <input type="color" v-model="config.branding.buttonStyle.borderColor" class="w-full h-8 rounded cursor-pointer" />
            </div>
            <div>
              <label class="text-[10px] text-slate-500 mb-1 block">Coins</label>
              <input type="number" v-model.number="config.branding.buttonStyle.borderRadius" class="w-full p-2 bg-slate-50 rounded-lg border text-xs" />
            </div>
          </div>
        </div>

        <!-- Auto-Select Timeout -->
        <div class="space-y-3 pt-4 border-t">
          <label class="text-[10px] font-black text-slate-400 uppercase">Sélection Auto</label>
          <div class="flex items-center gap-2">
            <input type="checkbox" v-model="config.autoSelectTimeout.enabled" id="autoSelect" />
            <label for="autoSelect" class="text-xs">Activer la sélection automatique</label>
          </div>

          <div v-if="config.autoSelectTimeout.enabled" class="space-y-2">
            <div>
              <label class="text-[10px] text-slate-500 mb-1 block">Délai (secondes)</label>
              <input type="number" v-model.number="config.autoSelectTimeout.seconds" class="w-full p-2 bg-slate-50 rounded-lg border text-xs" />
            </div>
          </div>
        </div>
      </div>
    </aside>

    <!-- Modal Ajouter Langue -->
    <Transition name="fade">
      <div v-if="showAddLanguageMenu" class="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-[100] flex items-center justify-center p-6">
        <div class="bg-white rounded-[2rem] p-8 max-w-md w-full shadow-2xl">
          <div class="flex justify-between items-center mb-6">
            <h2 class="text-xl font-bold text-slate-900">Ajouter une langue</h2>
            <button @click="showAddLanguageMenu = false" class="text-2xl text-slate-300">✕</button>
          </div>
          <div class="space-y-3">
            <button v-for="lang in availableLanguagesToAdd" :key="lang.code"
                    @click="addLanguage(lang)"
                    class="w-full flex items-center gap-3 p-4 rounded-xl border-2 border-slate-100 hover:border-blue-500 hover:bg-blue-50 transition-all">
              <span class="text-2xl">{{ lang.flag }}</span>
              <span class="font-semibold">{{ lang.label }}</span>
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { toast } from "vue3-toastify";
import { useRoute } from 'vue-router';
import MediaPicker from '@/components/MediaPicker.vue';

const SCALE_FACTOR = 1080 / 375;

const route = useRoute();
const agencyId = route.params.kioskId;

const showAddLanguageMenu = ref(false);

const allLanguages = [
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹' }
];

const config = ref({
  agencyId: agencyId,
  enabled: true,
  enabledLanguages: [
    { code: 'fr', label: 'Français', flag: '🇫🇷', order: 0 }
  ],
  defaultLanguage: 'fr',
  displayStyle: {
    type: 'cards',
    columns: 2,
    itemSize: 'large'
  },
  branding: {
    backgroundColor: '#F8FAFC',
    backgroundType: 'color',
    gradient: {
      color1: '#667eea',
      color2: '#764ba2',
      direction: 'to right'
    },
    backgroundImage: '',
    buttonStyle: {
      backgroundColor: '#FFFFFF',
      textColor: '#1E293B',
      hoverColor: '#3B82F6',
      borderRadius: 20,
      borderWidth: 2,
      borderColor: '#E2E8F0'
    },
    header: {
      show: true,
      text: 'Sélectionnez votre langue',
      fontSize: 48,
      fontFamily: 'Plus Jakarta Sans',
      color: '#1E293B'
    },
    logo: {
      show: false,
      url: '',
      size: 120,
      position: 'top'
    }
  },
  autoSelectTimeout: {
    enabled: false,
    seconds: 10,
    defaultLanguage: 'fr'
  },
  targetResolution: {
    width: 1080,
    height: 1920
  }
});

const availableLanguagesToAdd = computed(() => {
  return allLanguages.filter(lang =>
    !config.value.enabledLanguages.some(enabled => enabled.code === lang.code)
  );
});

const getBackgroundStyle = () => {
  if (config.value.branding.backgroundType === 'gradient') {
    return {
      background: `linear-gradient(${config.value.branding.gradient.direction}, ${config.value.branding.gradient.color1}, ${config.value.branding.gradient.color2})`
    };
  } else if (config.value.branding.backgroundType === 'image' && config.value.branding.backgroundImage) {
    return {
      backgroundImage: `url(${config.value.branding.backgroundImage})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center'
    };
  }
  return {
    backgroundColor: config.value.branding.backgroundColor
  };
};

const addLanguage = (lang) => {
  config.value.enabledLanguages.push({
    code: lang.code,
    label: lang.label,
    flag: lang.flag,
    order: config.value.enabledLanguages.length
  });
  showAddLanguageMenu.value = false;
  toast.success(`${lang.label} ajouté`);
};

const removeLanguage = (idx) => {
  if (config.value.enabledLanguages.length === 1) {
    toast.error("Au moins une langue doit être active");
    return;
  }
  config.value.enabledLanguages.splice(idx, 1);
};

const fetchConfig = async () => {
  try {
    const token = localStorage.getItem("superAdminToken");
    const res = await fetch(`https://api.datafuse.fr/api/super-admin/language-selection-config/${agencyId}`, {
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
    });
    if (res.status === 404) {
      return;
    }
    const data = await res.json();
    config.value = data;
  } catch (err) {
    toast.error("Erreur chargement config : " + err.message);
  }
};

const saveConfig = async () => {
  const idToast = toast.loading("Enregistrement...");
  try {
    const token = localStorage.getItem("superAdminToken");
    const res = await fetch(`https://api.datafuse.fr/api/super-admin/language-selection-config/${agencyId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(config.value)
    });
    if (!res.ok) throw new Error("Erreur sauvegarde");
    toast.update(idToast, { render: "🚀 Configuration publiée !", type: "success", isLoading: false, autoClose: 3000 });
  } catch (err) {
    toast.update(idToast, { render: err.message, type: "error", isLoading: false, autoClose: 3000 });
  }
};

// === EXPORT/IMPORT ===
const exportConfig = () => {
  const dataStr = JSON.stringify(config.value, null, 2);
  const dataBlob = new Blob([dataStr], { type: 'application/json' });
  const url = URL.createObjectURL(dataBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `language-selection-design-${Date.now()}.json`;
  link.click();
  URL.revokeObjectURL(url);
  toast.success("Design exporté!");
};

const importConfig = (event) => {
  const file = event.target.files[0];
  if (!file) return;

  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      const imported = JSON.parse(e.target.result);

      // Conserver l'agencyId actuel
      imported.agencyId = config.value.agencyId;

      // Charger la configuration importée
      config.value = imported;

      toast.success("Design importé!");

      // Réinitialiser l'input file pour permettre de réimporter le même fichier
      event.target.value = '';
    } catch (err) {
      toast.error("Fichier invalide");
      event.target.value = '';
    }
  };
  reader.readAsText(file);
};

onMounted(() => {
  fetchConfig();
});
</script>

<style scoped>
.font-sans { font-family: 'Plus Jakarta Sans', sans-serif; }
.fade-enter-active, .fade-leave-active { transition: opacity 0.3s; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
</style>
